import { CheckCircle2, FileText, Link2, RefreshCw, RotateCcw, Save, Unplug } from 'lucide-react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { CountdownPanel } from './CountdownPanel';
import {
  disconnectTwitchTextOutputs,
  loadTextOutputs,
  refreshTextOutputs,
  resetTextOutputSession,
  updateTextOutput,
  updateTextOutputSettings,
  type TextOutputsSnapshot,
} from './text-outputs-api';
import './text-outputs.css';

function count(value: number | null | undefined): string {
  return value === null || value === undefined ? '—' : value.toLocaleString('en-GB');
}

function when(value: string | null | undefined): string {
  if (!value) return '—';
  const date = new Date(value);
  return Number.isFinite(date.getTime()) ? date.toLocaleString('en-GB') : '—';
}

function validRecentListLimit(value: string): number | null {
  if (!/^\d+$/.test(value.trim())) return null;
  const parsed = Number(value);
  return Number.isInteger(parsed) && parsed >= 1 && parsed <= 25 ? parsed : null;
}

function visibleDefinitions(snapshot: TextOutputsSnapshot | null) {
  return (snapshot?.definitions ?? []).filter((item) => item.key !== 'countdown_control');
}

export function TextOutputsPage({ role }: { role: 'owner' | 'operator' }) {
  const [snapshot, setSnapshot] = useState<TextOutputsSnapshot | null>(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState('');
  const [selectedKey, setSelectedKey] = useState('');
  const [filename, setFilename] = useState('');
  const [template, setTemplate] = useState('');
  const [outputDirectory, setOutputDirectory] = useState('');
  const [recentListLimitDraft, setRecentListLimitDraft] = useState('');

  const refresh = useCallback(async () => {
    try {
      const next = await loadTextOutputs();
      const visible = next.definitions.filter((item) => item.key !== 'countdown_control');
      setSnapshot(next);
      setOutputDirectory(next.settings.outputDirectory);
      setRecentListLimitDraft(String(next.settings.recentListLimit));
      setSelectedKey((current) =>
        current && visible.some((item) => item.key === current)
          ? current
          : visible[0]?.key ?? '',
      );
      setMessage('');
    } catch (error) {
      setMessage(error instanceof Error ? error.message : 'Text Outputs could not be loaded.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { void refresh(); }, [refresh]);

  const definitions = useMemo(() => visibleDefinitions(snapshot), [snapshot]);
  const selected = useMemo(
    () => definitions.find((item) => item.key === selectedKey) ?? null,
    [definitions, selectedKey],
  );

  useEffect(() => {
    if (!selected) return;
    setFilename(selected.filename);
    setTemplate(selected.template);
  }, [selected?.key, selected?.filename, selected?.template]);

  async function apply(operation: () => Promise<TextOutputsSnapshot>, success: string) {
    setBusy(true);
    setMessage('');
    try {
      const next = await operation();
      setSnapshot(next);
      setOutputDirectory(next.settings.outputDirectory);
      setMessage(success);
    } catch (error) {
      setMessage(error instanceof Error ? error.message : 'Text Output action failed.');
    } finally {
      setBusy(false);
    }
  }

  async function saveSelected() {
    if (!selected) return;
    await apply(
      () => updateTextOutput(selected.key, { filename: filename.trim(), template }),
      `${selected.label} saved.`,
    );
  }

  async function saveDirectory() {
    if (!snapshot) return;
    await apply(
      () => updateTextOutputSettings({ outputDirectory: outputDirectory.trim() }),
      'Output folder updated.',
    );
  }

  async function saveRecentListLimit() {
    const limit = validRecentListLimit(recentListLimitDraft);
    if (limit === null) {
      setMessage('Recent-list length must be a whole number from 1 to 25.');
      return;
    }
    await apply(
      () => updateTextOutputSettings({ recentListLimit: limit }),
      'Recent-list length updated.',
    );
    setRecentListLimitDraft(String(limit));
  }

  async function disconnectTwitch() {
    if (role !== 'owner') return;
    if (!window.confirm('Disconnect Twitch from FFSControl Text Outputs?')) return;
    setBusy(true);
    setMessage('');
    try {
      await disconnectTwitchTextOutputs();
      await refresh();
      setMessage('Twitch disconnected from Text Outputs.');
    } catch (error) {
      setMessage(error instanceof Error ? error.message : 'Twitch could not be disconnected.');
    } finally {
      setBusy(false);
    }
  }

  if (loading && !snapshot) return <div className="ffs-next-loading">Loading Text Outputs…</div>;

  const twitch = snapshot?.twitch;
  const agentOnline = snapshot?.activeAgentConnected === true && snapshot.agent.status === 'online';
  const recentListLimitValue = validRecentListLimit(recentListLimitDraft);

  return (
    <div className="ffs-workspace-page ffs-text-page">
      {message ? <div className="ffs-workspace-message">{message}</div> : null}

      <section className="ffs-workspace-hero">
        <div><small>OBS TEXT FILES</small><h2>Text Outputs</h2><p>See the live values first, then control which text files FFSControl writes for OBS.</p></div>
        <div className="ffs-text-hero-actions">
          <span className={`ffs-status ${agentOnline ? 'good' : 'warning'}`}>{agentOnline ? 'WRITER ONLINE' : 'WRITER NEEDS ATTENTION'}</span>
          <button type="button" className="ffs-button secondary" disabled={busy} onClick={() => void apply(refreshTextOutputs, 'Twitch values and files refreshed.')}><RefreshCw size={15} /> Refresh Values</button>
        </div>
      </section>

      <section className="ffs-text-live-grid">
        <LiveMetric label="Viewers" value={twitch?.live ? count(twitch.currentViewers) : 'OFFLINE'} detail="current Twitch viewers" />
        <LiveMetric label="Subscribers" value={count(twitch?.currentSubTotal)} detail="current active total" />
        <LiveMetric label="Followers" value={count(twitch?.followerTotal)} detail="channel total" />
        <LiveMetric label="Now playing" value={snapshot?.agent.nowPlaying ?? 'Nothing playing'} detail="current media text" compact />
      </section>

      {role === 'owner' ? <CountdownPanel /> : null}

      <section className="ffs-text-main-grid">
        <article className="ffs-workspace-card ffs-text-connection-card">
          <header><div><small>CONNECTIONS</small><h3>Output writer</h3></div><FileText size={18} /></header>
          <div className="ffs-text-facts">
            <Fact label="Stream PC" value={snapshot?.activeAgentName ?? 'No active Agent'} detail={snapshot?.activeAgentConnected ? 'Connected' : 'Offline'} />
            <Fact label="Writer" value={snapshot?.agent.status ?? 'unknown'} detail={`${snapshot?.agent.writtenFiles ?? 0} files written this run`} />
            <Fact label="Last write" value={when(snapshot?.agent.lastWriteAt)} detail={snapshot?.agent.outputDirectory ?? snapshot?.settings.outputDirectory ?? 'No output folder'} />
          </div>
          {snapshot?.agent.lastError ? <div className="ffs-text-warning">{snapshot.agent.lastError}</div> : null}
          <label className="ffs-text-directory"><span>Windows output folder</span><div><input value={outputDirectory} onChange={(event) => setOutputDirectory(event.target.value)} /><button type="button" className="ffs-button secondary" disabled={busy || !outputDirectory.trim() || outputDirectory === snapshot?.settings.outputDirectory} onClick={() => void saveDirectory()}><Save size={14} /> Save Folder</button></div></label>
        </article>

        <article className="ffs-workspace-card ffs-text-twitch-card">
          <header><div><small>TWITCH DATA</small><h3>Channel connection</h3></div><Link2 size={18} /></header>
          <div className="ffs-text-twitch-state">
            <strong>{twitch?.connected ? twitch.broadcasterName ?? 'Connected' : 'Not connected'}</strong>
            <span>{twitch?.live ? 'Stream live' : 'Stream offline'}{twitch?.gameName ? ` · ${twitch.gameName}` : ''}</span>
            <small>{twitch?.streamTitle ?? 'No current stream title'}</small>
          </div>
          {twitch?.lastError ? <div className="ffs-text-warning">{twitch.lastError}</div> : null}
          <div className="ffs-inline-actions">
            {role === 'owner' ? (
              !twitch?.connected
                ? <button type="button" className="ffs-button primary" onClick={() => window.location.assign('/api/v1/integrations/twitch/connect')}><Link2 size={14} /> Connect Twitch</button>
                : <button type="button" className="ffs-button warning" disabled={busy} onClick={() => void disconnectTwitch()}><Unplug size={14} /> Disconnect Twitch</button>
            ) : <span className="ffs-text-refresh-meta">Twitch account authorisation is managed by the owner.</span>}
            <span className="ffs-text-refresh-meta">Last update: {when(twitch?.lastRefreshAt ?? twitch?.lastEventAt)}</span>
          </div>
        </article>
      </section>

      <section className="ffs-text-output-workspace">
        <article className="ffs-workspace-card ffs-text-list-card">
          <header><div><small>{definitions.length} OUTPUTS</small><h3>Files</h3></div><span>{definitions.filter((item) => item.enabled).length} enabled</span></header>
          <div className="ffs-text-output-list">
            {definitions.map((definition) => (
              <button type="button" key={definition.key} className={`${definition.key === selectedKey ? 'selected' : ''} ${definition.enabled ? 'enabled' : ''}`} onClick={() => setSelectedKey(definition.key)}>
                <div><strong>{definition.label}</strong><span>{definition.filename}</span></div><small>{definition.enabled ? 'ON' : 'OFF'}</small>
              </button>
            ))}
          </div>
        </article>

        <article className="ffs-workspace-card ffs-text-editor-card">
          {selected ? <>
            <header><div><small>OUTPUT DETAIL</small><h3>{selected.label}</h3></div><button type="button" className={`ffs-button ${selected.enabled ? 'warning' : 'primary'}`} disabled={busy} onClick={() => void apply(() => updateTextOutput(selected.key, { enabled: !selected.enabled }), `${selected.label} ${selected.enabled ? 'disabled' : 'enabled'}.`)}>{selected.enabled ? 'Disable' : 'Enable'}</button></header>
            <div className="ffs-text-preview"><span>Current file contents</span><strong>{selected.preview || 'Blank'}</strong></div>
            <div className="ffs-text-editor-fields">
              <label><span>Filename</span><input value={filename} onChange={(event) => setFilename(event.target.value)} /></label>
              <label><span>Format</span><textarea rows={3} value={template} onChange={(event) => setTemplate(event.target.value)} /></label>
              <p>{selected.description}</p>
              <small>Available where supported: {'{value}'} · {'{name}'} · {'{count}'} · {'{bits}'} · {'{viewers}'}</small>
              <button type="button" className="ffs-button primary" disabled={busy || !filename.trim()} onClick={() => void saveSelected()}><Save size={14} /> Save Output</button>
            </div>
          </> : <div className="ffs-empty-state">Select a Text Output to edit it.</div>}
        </article>
      </section>

      <section className="ffs-workspace-card ffs-text-settings-card">
        <header><div><small>GLOBAL SETTINGS</small><h3>Formatting &amp; session</h3></div><CheckCircle2 size={18} /></header>
        {snapshot ? <div className="ffs-text-settings-grid">
          <button type="button" className={`ffs-choice-card ${snapshot.settings.enabled ? 'selected' : ''}`} disabled={busy} onClick={() => void apply(() => updateTextOutputSettings({ enabled: !snapshot.settings.enabled }), `Text Outputs ${snapshot.settings.enabled ? 'disabled' : 'enabled'}.`)}><strong>Writer {snapshot.settings.enabled ? 'On' : 'Off'}</strong><span>Master file-output switch</span></button>
          <label><span>Time format</span><select value={snapshot.settings.timeFormat} disabled={busy} onChange={(event) => void apply(() => updateTextOutputSettings({ timeFormat: event.target.value as TextOutputsSnapshot['settings']['timeFormat'] }), 'Time format updated.')}><option value="HH:mm">HH:mm</option><option value="HH:mm:ss">HH:mm:ss</option></select></label>
          <label><span>Date format</span><select value={snapshot.settings.dateFormat} disabled={busy} onChange={(event) => void apply(() => updateTextOutputSettings({ dateFormat: event.target.value as TextOutputsSnapshot['settings']['dateFormat'] }), 'Date format updated.')}><option value="DD/MM/YYYY">DD/MM/YYYY</option><option value="ddd DD/MM/YYYY">ddd DD/MM/YYYY</option><option value="D MMMM YYYY">D MMMM YYYY</option></select></label>
          <label><span>Viewers while offline</span><select value={snapshot.settings.viewerOfflineMode} disabled={busy} onChange={(event) => void apply(() => updateTextOutputSettings({ viewerOfflineMode: event.target.value as TextOutputsSnapshot['settings']['viewerOfflineMode'] }), 'Offline viewer behaviour updated.')}><option value="zero">Write 0</option><option value="blank">Write blank</option><option value="retain">Keep last value</option></select></label>
          <label className="ffs-text-number-setting"><span>Recent-list length</span><div><input type="number" min="1" max="25" value={recentListLimitDraft} aria-invalid={recentListLimitValue === null} disabled={busy} onChange={(event) => setRecentListLimitDraft(event.target.value)} /><button type="button" className="ffs-button secondary" disabled={busy || recentListLimitValue === null || recentListLimitValue === snapshot.settings.recentListLimit} onClick={() => void saveRecentListLimit()}><Save size={13} /> Save</button></div></label>
        </div> : null}
        <div className="ffs-text-session-row"><div><strong>Session counters</strong><span>Current session began {when(snapshot?.settings.sessionStartedAt)}.</span></div><button type="button" className="ffs-button warning" disabled={busy} onClick={() => { if (window.confirm('Reset the session follow, sub, gift, Bits and raid counters to zero?')) void apply(resetTextOutputSession, 'Text Output session counters reset.'); }}><RotateCcw size={14} /> Reset Session Counters</button></div>
      </section>
    </div>
  );
}

function LiveMetric({ label, value, detail, compact = false }: { label: string; value: string; detail: string; compact?: boolean }) {
  return <article className={`ffs-text-live-metric ${compact ? 'compact' : ''}`}><span>{label}</span><strong>{value}</strong><small>{detail}</small></article>;
}

function Fact({ label, value, detail }: { label: string; value: string; detail: string }) {
  return <div><span>{label}</span><strong>{value}</strong><small>{detail}</small></div>;
}
